<template>
  <ts-card>
    <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
      <h4 class="ts-text-sm">{{ $t('payMonthlyIncentiveBonus.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper>
      <ts-loading-banner :loading="loadingPreData">
        <form>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{
                  $t('payMonthlyIncentiveBonus.employeeId')
                }}</label>
                <div>
                  <ts-select
                    v-model="model.employee_id"
                    :options="
                      employees.map(e => ({
                        text: e.employee_name_en,
                        value: e.employee_id
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('employee_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('employee_id')">{{ errors.first('employee_id') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{
                  $t('payMonthlyIncentiveBonus.incBonusId')
                }}</label>
                <div>
                  <ts-select
                    v-model="model.inc_bonus_id"
                    :options="
                      incentiveBonuses.map(e => ({
                        text: e.inc_bonus_item,
                        value: e.inc_bonus_id
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('inc_bonus_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('inc_bonus_id')">
                    {{ errors.first('inc_bonus_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{
                  $t('payMonthlyIncentiveBonus.incBonus')
                }}</label>
                <div>
                  <ts-input
                    type="number"
                    v-model.number="model.inc_bonus"
                    :class="{ 'is-invalid': errors.has('inc_bonus') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('inc_bonus')">{{ errors.first('inc_bonus') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{
                  $t('payMonthlyIncentiveBonus.cycleYear')
                }}</label>
                <div>
                  <ts-input v-model="model.cycle_year" :class="{ 'is-invalid': errors.has('cycle_year') }" />
                  <div class="invalid-feedback" v-if="errors.has('cycle_year')">{{ errors.first('cycle_year') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{
                  $t('payMonthlyIncentiveBonus.cycleMonth')
                }}</label>
                <div>
                  <ts-input v-model="model.cycle_month" :class="{ 'is-invalid': errors.has('cycle_month') }" />
                  <div class="invalid-feedback" v-if="errors.has('cycle_month')">{{ errors.first('cycle_month') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label">{{ $t('payMonthlyIncentiveBonus.description') }}</label>
                <div>
                  <ts-textarea v-model="model.description" rows="3" />
                </div>
              </div>
            </div>
          </div>
          <div class="row ts-mt-6">
            <div class="col-sm-12 col-lg-12 col-md-12 ts-text-right">
              <ts-button
                outline
                color="primary"
                class="ml-2"
                :waiting="savingAndNew"
                @click.prevent="saveAndNew"
                v-if="editData === undefined"
                >{{ $t('button.createAndNew') }}</ts-button
              >
              <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">
                {{ editData ? $t('button.update') : $t('button.create') }}
              </ts-button>
            </div>
          </div>
        </form>
      </ts-loading-banner>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { cloneDeep } from 'lodash'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'pay-monthly-incentive-bonus-form',
  props: ['editData'],
  data() {
    const model = {
      employee_id: this.editData ? this.editData.employee_id : '',
      inc_bonus_id: this.editData ? this.editData.inc_bonus_id : '',
      inc_bonus: this.editData ? this.editData.inc_bonus : '',
      cycle_year: this.editData ? this.editData.cycle_year : '',
      cycle_month: this.editData ? this.editData.cycle_month : '',
      description: this.editData ? this.editData.description : ''
    }

    return {
      newModel: model,
      model: cloneDeep(model),
      errors: new Errors(),
      saving: false,
      savingAndNew: false,
      loadingPreData: false
    }
  },
  computed: {
    ...mapState('hr/payMonthlyIncentiveBonus', ['employees', 'incentiveBonuses']),
    dayRange: vm => (start, end) => {
      return range(start, end)
    }
  },
  created() {
    this.loadingPreData = true

    Promise.all([this.fetchEmployees(), this.fetchIncentiveBonuses()]).then(responses => {
      this.loadingPreData = false
    })
  },
  methods: {
    ...mapActions('hr/payMonthlyIncentiveBonus', ['fetchEmployees', 'fetchIncentiveBonuses']),
    submit() {
      let responseResult

      this.errors = new Errors()

      if (this.editData) {
        responseResult = this.$store.dispatch('hr/payMonthlyIncentiveBonus/update', {
          id: this.editData.record_id,
          ...this.model
        })
      } else {
        responseResult = this.$store.dispatch('hr/payMonthlyIncentiveBonus/create', this.model)
      }

      return responseResult
        .then(response => {
          return this.$store.dispatch('hr/payMonthlyIncentiveBonus/fetch').then(res => {
            this.saving = false
            this.$toasted.success(response.message)
            return Promise.resolve(res)
          })
        })
        .catch(error => {
          if (error.errors) this.errors = new Errors(error.errors)
          else this.$toasted.error(error.message)

          return Promise.reject(error)
        })
    },
    save() {
      this.saving = true

      this.submit()
        .then(() => {
          this.$emit('close')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAndNew() {
      this.savingAndNew = true

      this.submit()
        .then(() => {
          this.model = cloneDeep(this.newModel)
        })
        .finally(() => {
          this.savingAndNew = false
        })
    }
  }
}
</script>
